import {
  Cartesian2,
  Cartesian3,
  Color,
  VerticalOrigin,
  createGuid,
  Event,
  defined,
  JulianDate,
  BoundingSphere,
  Matrix4,
  HeadingPitchRoll,
  Transforms,
} from "@cesium/engine";
import { ConstantUrl, Utils } from "@cesium/widgets";
import DitapViewer from "../basic/DitapViewer";
import EditMove from "./EditMove";
import EditScale from "./EditScale";

const FLAG_PIXEL_OFFSET = new Cartesian2(15, 0);
const PIN_PIXEL_OFFSET = new Cartesian2(0, 0);
const BALLOON_PIXEL_OFFSET = new Cartesian2(0, 0);

/**
 * position 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const positionChangedHandler = (parent, newValue) => {
  const entity = parent.pointEntity;
  if (defined(entity)) {
    EditMove.setPosition(entity, newValue);
  }
};

/**
 * scale 변경 핸들러
 * @param {object} parent
 * @param {*} newValue
 * @param {*} oldValue
 */
const scaleChangedHandler = (parent, newValue) => {
  const entity = parent.pointEntity;
  if (defined(entity)) {
    EditScale.setScale(entity, newValue);
  }
};

/**
 * DefinitionChanged 이벤트 핸들러
 * @param {object} parent 부모 객체
 * @param {string} property 프로퍼티명
 * @param {*} newValue 변경값
 * @param {*} oldValue 이전값
 */
const definitionChangedHandler = (parent, property, newValue) => {
  if (property === "position") {
    positionChangedHandler(parent, newValue);
  } else if (property === "scale") {
    scaleChangedHandler(parent, newValue);
  }
};

/**
 * 점 엔티티 표시 클래스.</br>
 * Point entity display class.
 *
 * @alias PointEntity
 * @constructor
 *
 * @param {DitapViewer} viewer 사용할 Ditap Viewer 인스턴스.</br> The DitapViewer instance to use.
 * @param {Cartesian3} [position] 점 생성을 위한 Cartesian 좌표.</br> Cartesian coordinates for point creation.
 *
 * @example
 *
 * // 샌드캐슬 예제 'DiTAP - Point Entity' 참조
 *
 * const viewer = new DitapViewer("ditapContainer");
 *
 * // 1. 위치 지정 없이 객체 생성. (InteractionPointEntity를 통해 점 엔티티 표시.)
 * const PointEntity = new Ditap.PointEntity(viewer);
 *
 * const InteractionPointEntity = new Ditap.InteractionPointEntity();
 * InteractionPointEntity.PointEntity = PointEntity;
 *
 * // 2. 위치 지정하여 인스턴스 생성. (InteractionPointEntity 통한 수정 불가.)
 * const position = new Ditap.Cartesian3(-3044793.4521523346, 4043760.1510156766, 3867436.511895938);
 * const PointEntity = new Ditap.PointEntity(viewer, position);
 *
 */
class PointEntity {
  constructor(viewer, position) {
    this._id = `PointEntity${createGuid()}`;
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
      viewer.ditapStorage.pointEntity[this.id] = this;
    } else {
      console.error("Type doesn't match");
    }

    this._pointEntity = undefined;

    // if (position instanceof Cartesian3) {
    //   this._position = position;
    //   this.createEntity();
    // } else {
    //   this._position = undefined;
    // }
    this._displayCondition = 2000;

    // ICON URL
    this.flagIconUrl = Utils.getUrlFromBase64(ConstantUrl.FLAG_ICON_URL);
    this.pinIconUrl = Utils.getUrlFromBase64(ConstantUrl.PIN_ICON_URL);
    this.balloonIconUrl = Utils.getUrlFromBase64(ConstantUrl.BALLOON_ICON_URL);

    // PointEntity Property 값 변경 감지 이벤트
    this._definitionChanged = new Event();

    // posiiton, scale 멤버변수 정의
    this._position = undefined;
    this._scale = undefined;

    // position, scale Property 정의
    Object.defineProperties(this, {
      /**
       * Point Cartesian 좌표. </br>
       * Point Cartesian coordinates.
       * @type {PositionProperty|undefined}
       */
      position: Utils.createPositionPropertyDescriptor("position"),

      /**
       * 포인트 크기 (px). </br>
       * Point Size (px).
       * @type {PositionProperty|undefined}
       */
      scale: Utils.createPositionPropertyDescriptor("scale"),
    });

    // position, scale 값 변경 이벤트 핸들러 추가
    this._definitionChanged.addEventListener(definitionChangedHandler);

    // position 초기화
    if (position instanceof Cartesian3 || defined(position?.getValue)) {
      this.position = position;
      this.createEntity();
    }
  }

  /**
   * 사용할 Ditap Viewer 인스턴스.</br>
   * The DitapViewer instance to use.
   * @type {DitapViewer}
   */
  set viewer(viewer) {
    if (viewer instanceof DitapViewer) {
      this._viewer = viewer;
    }
  }
  get viewer() {
    return this._viewer;
  }

  /*
   * 포인트 엔티티 ID.</br>
   * ID to assign to the PointEntity.
   * @type {string}
   */
  set id(id) {
    this._id = id;
  }
  get id() {
    return this._id;
  }

  /**
   * 점 생성으로 얻은 포인트 엔티티.</br>
   * Point Entity obtained by pointEntity creation.
   * @type {Entity|undefined}
   * @readonly
   */
  set pointEntity(pointEntity) {
    this._pointEntity = pointEntity;
  }
  get pointEntity() {
    return this._pointEntity;
  }

  /*
   * 엔티티의 DistanceDisplayCondition 값.</br>
   * DistanceDisplayCondition value of entity.
   * @type {number}
   */
  set displayCondition(displayCondition) {
    this._displayCondition = displayCondition;
  }
  get displayCondition() {
    return this._displayCondition;
  }

  /*
   * 포인트 엔티티 표시 여부.</br>
   * set the show value of pointEntity
   * @param {boolean} flag 표시 여부 값.</br> The value of show
   */
  setPointShow(flag) {
    if (this.pointEntity.point) {
      this.pointEntity.point.show = flag;
    }
  }

  /*
   * 빌보드 표시 여부.</br>
   * Set the show value of billboard.
   * @param {boolean} flag 표시 여부 값.</br> The value of show.
   */
  setBillboardShow(flag) {
    if (this.pointEntity.billboard) {
      this.pointEntity.billboard.show = flag;
    }
  }

  /*
   * 포인트 엔티티 아웃라인 색상 설정.</br>
   * Set the outline color of pointEntity.
   * @param {Entity} pointEntity 색상을 변경할 엔티티.</br> The Entity to change color.
   * @param {Color} color 변경할 색상.</br> The color to change.
   */
  setPointOutLineColor(color) {
    this.pointEntity.point.outlineColor = color;
  }

  /*
   * 포인트 엔티티 픽셀 사이즈 설정.</br>
   * Set pixel size of pointEntitiy.
   * @param {Entity} pointEntity 픽셀 사이즈를 변경할 엔티티.</br> The pointEntity to change pixelSize.
   * @param {number} size 픽셀 값.</br> The pixel size to change.
   */
  setPointPixelSize(size) {
    this.pointEntity.point.pixelSize = size;
  }

  /*
   * 빌보드 이미지 설정.</br>
   * Set image of billboard.
   * @param {string} image 변경할 이미지 url.</br> The image Url to change.
   */
  setBillboardImage(image) {
    this.pointEntity.billboard.image = image;
  }

  /*
   * 빌보드 타입 설정.</br>
   * Set type of billboard.
   * @param {string} type 빌보드 타입.</br> The type of billboard(ex: "flag", "pin", "balloon").
   */
  setBillboardType(type) {
    this.pointEntity.billboard.type = type;
  }

  /*
   * 빌보드 타입 조회.</br>
   * Get type of billboard.
   * @returns {string} 조회된 빌보드 타입.</br> The type of billboard(ex: "flag", "pin", "balloon").
   */
  getBillboardType() {
    return this.pointEntity.billboard.type;
  }

  /*
   * 빌보드 vertical 속성 설정.</br>
   * Set verticalOrigin of billboard.
   * @param {number} verticalOrigin 변경할 vertical 속성.</br> The vertical origin.</br> (ex: VerticalOrigin.CENTER, VerticalOrigin.BOTTOM, VerticalOrigin.BASELINE, VerticalOrigin.TOP).
   */
  setBillboardVerticalOrigin(verticalOrigin) {
    this.pointEntity.billboard.verticalOrigin = verticalOrigin;
  }

  /*
   * 빌보드 픽셀 오프셋 설정.</br>
   * Set pixelOffset of billboard.
   * @param {Cartesian2} cartesian 변경할 픽셀 오프셋 값.</br> The billboards's pixel offset in screen space.
   */
  setBillboardPixelOffset(position) {
    this.pointEntity.billboard.pixelOffset = position;
  }

  /*
   * 포인트 엔티티 스케일 값 설정.</br>
   * Set scale of pointEntitiy.
   * @param {number} scale 변경할 스케일 값.</br> The scale to change.
   */
  setBillboardScale(scale) {
    this.pointEntity.billboard.scale = Number(scale);
  }

  /*
   * 포인트 엔티티의 빌보드 설정.</br>
   * Set billboard of pointEntitiy.
   * @param {string} type 빌보드 타입.</br> The type of billboard(ex: "flag", "pin", "balloon").
   */
  setBillboard(type) {
    let image;

    switch (type) {
      case "flag":
        image = this.flagIconUrl;
        this.setBillboardType("flag");
        this.setBillboardPixelOffset(FLAG_PIXEL_OFFSET);
        break;
      case "pin":
        image = this.pinIconUrl;
        this.setBillboardType("pin");
        this.setBillboardPixelOffset(PIN_PIXEL_OFFSET);
        break;
      case "balloon":
        image = this.balloonIconUrl;
        this.setBillboardType("balloon");
        this.setBillboardPixelOffset(BALLOON_PIXEL_OFFSET);
        break;
      default:
        break;
    }

    this.setBillboardShow(true);
    this.setPointShow(false);
    this.setBillboardImage(image);
    this.setBillboardVerticalOrigin(VerticalOrigin.BOTTOM);
    this.setBillboardScale("0.8");
  }

  /**
   * 인스턴스 파괴. 영구적으로 제거하는 경우 호출.</br>
   * Destroys the instance. Should be called if permanently removing the instance from layout.
   */
  destroy() {
    // entity 제거
    this.viewer.entities.remove(this.pointEntity);

    // 변수 초기화
    this.pointEntity = undefined;
    this.id = undefined;
  }

  /**
   * Ditap Viewer에 pointEntity 생성.</br>
   * Create a pointEntity in the DitapViewer.
   */
  createEntity() {
    const position = Utils.getPropertyValue(this.position);
    const displayCondition = Utils.getDistanceDisplayConditon([position]);

    if (displayCondition > 2000) {
      this.displayCondition = displayCondition;
    }

    // Entity 생성
    const entity = Utils.createPoint({
      viewer: this.viewer,
      id: this.id,
      position: position,
      outlineColor: Color.fromBytes(240, 1, 1, 255),
      displayCondition: this.displayCondition,
      pixelSize: this.scale,
      //changedCallback: (entity, property, newValue, oldValue) => {}
    });

    this.pointEntity = entity;
  }

  /**
   * 지정된 시간에 엔티티에 대한 positions 계산. <br>
   * Calculate positions for an entity at a given time.
   * @param {JulianDate} [time=JulianDate] time 모델 positions를 검색하는 시간. <br> The time to retrieve model positions for.
   * @returns {positions | undefined} Model positions. <br> Model positions.
   */
  computePositions(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    const pointEntity = this.pointEntity;
    if (!(position && pointEntity)) {
      return;
    }

    return [position];
  }

  /**
   * 지정된 시간에 엔티티에 대한 BoundingSphere 계산. <br>
   * Calculate BoundingSphere for an entity at a given time.
   * @param {JulianDate} [time=JulianDate] time 모델 BoundingSphere를 검색하는 시간. <br> The time to retrieve model BoundingSphere for.
   * @returns {BoundingSphere | undefined} Model BoundingSphere. <br> Model BoundingSphere.
   */
  computeBoundingSphere(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    const pointEntity = this.pointEntity;
    if (!(position && pointEntity)) {
      return;
    }

    // point pixelsize * scale = BoundignSphere radius
    const pixelSize = pointEntity.point.pixelSize || 8;
    const scale = Utils.getPropertyValue(this.scale) || Cartesian3.ONE;
    const maximumComponent = Cartesian3.maximumComponent(scale);

    const radius = (pixelSize * maximumComponent) / 2;

    const boundingSphere = new BoundingSphere(position, radius);

    return boundingSphere;
  }

  /**
   * 지정된 시간에 엔티티 변환에 대한 모델 행렬 계산. <br>
   * Compute model matrix for entity transformation at specified time.
   * @param {JulianDate} [time=JulianDate] time 모델 행렬을 검색하는 시간. <br> The time to retrieve model matrix for.
   * @returns {Matrix4 | undefined} 모델 행렬. <br> Model matrix.
   */
  computeModelMatrix(time = new JulianDate()) {
    const position = Utils.getPropertyValue(this.position, time);
    if (!defined(position)) {
      return;
    }

    const orientation = Transforms.headingPitchRollQuaternion(
      position,
      new HeadingPitchRoll(0, 0, 0)
    );
    const scale = Utils.getPropertyValue(this.scale, time) || Cartesian3.ONE;

    const matrix = Matrix4.fromTranslationQuaternionRotationScale(
      position,
      orientation,
      scale,
      new Matrix4()
    );

    return matrix;
  }
}
export default PointEntity;
